import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { SESSION_STORAGE_KEYS } from '../constants/sessionStorageKeys';
import {
  glacierPolicyApiGetAgentAuthorityOptions,
  glacierPolicyApiGetPolicyOptions,
  glacierPolicyApiGetPolicyPermissionsOptions,
  glacierUsersApiCurrentUserOptions,
} from '../heyapi/@tanstack/react-query.gen';
import { usePermissionsStore } from './usePermissionsStore';

interface UseInitializePermissionsStoreProps {
  policyId: string | undefined;
}

export const useInitializePermissionsStore = ({
  policyId,
}: UseInitializePermissionsStoreProps) => {
  const {
    setCurrentUser,
    setEditMode,
    setIsTimeTraveling,
    setAgentAuthorityFlags,
    setIsPolicyAssignedToMe,
    setIsPolicyReferredToMe,
  } = usePermissionsStore();

  // Queries
  // current user
  const { data: currentUser, isSuccess: isCurrentUserSuccess } = useQuery({
    ...glacierUsersApiCurrentUserOptions(),
  });
  // policy data
  const { data: policyData, isSuccess: isPolicySuccess } = useQuery({
    ...glacierPolicyApiGetPolicyOptions({
      path: { account_id: policyId ?? '' },
    }),
    enabled: Boolean(policyId),
  });

  // agent authority data
  const { data: agentAuthorityData, isSuccess: isAgentAuthoritySuccess } =
    useQuery({
      ...glacierPolicyApiGetAgentAuthorityOptions({
        path: { account_id: policyId ?? '' },
      }),
      enabled: Boolean(policyId),
    });

  // policy permissions data
  const { data: permissionsData, isSuccess: isPermissionsSuccess } = useQuery({
    ...glacierPolicyApiGetPolicyPermissionsOptions({
      path: { account_id: policyId ?? '' },
    }),
    enabled: Boolean(policyId),
  });

  // - `isPolicyAssignedToMe` - Whether the current policy is assigned to the user
  // - `isPolicyReferredToMe` - Whether the policy was referred to the user
  const isPolicyAssignedToMe =
    policyData?.assignedTo?.userId === currentUser?.id;
  const isPolicyReferredToMe =
    isPolicyAssignedToMe && policyData?.referredBy != null;

  // Sync Current User to store
  useEffect(() => {
    if (currentUser && isCurrentUserSuccess) {
      const currentUserParsedData = {
        id: currentUser?.id,
        email: currentUser?.email || '',
        firstName: currentUser?.firstName || '',
        lastName: currentUser?.lastName || '',
        role: currentUser?.role || null,
      };
      setCurrentUser(currentUserParsedData);
    }
  }, [isCurrentUserSuccess, setCurrentUser, currentUser]);

  // Sync Edit Mode and Is Travel Time to store
  useEffect(() => {
    if (!isPermissionsSuccess) return;
    const isTimeTraveling = Boolean(
      sessionStorage.getItem(SESSION_STORAGE_KEYS.SEQUENCE_ID)
    );

    // Time travel mode: read-only, no editing allowed
    if (isTimeTraveling) {
      setIsTimeTraveling(true);
      setEditMode(false);
      return;
    }

    // Normal mode: no time travel
    setIsTimeTraveling(false);

    // Set edit mode based on policy permissions
    if (permissionsData === 'WRITE') {
      setEditMode(true);
    }

    if (permissionsData === 'READ' || permissionsData === 'UNAUTHORIZED') {
      setEditMode(false);
    }
  }, [isPermissionsSuccess, permissionsData, setEditMode, setIsTimeTraveling]);

  useEffect(() => {
    if (!isPolicySuccess) return;
    if (policyData && currentUser?.id) {
      setIsPolicyAssignedToMe(isPolicyAssignedToMe);
      setIsPolicyReferredToMe(isPolicyReferredToMe);
    }
  }, [
    currentUser?.id,
    isPolicyAssignedToMe,
    isPolicyReferredToMe,
    isPolicySuccess,
    policyData,
    setIsPolicyAssignedToMe,
    setIsPolicyReferredToMe,
  ]);

  // Sync Agent Authority Flags to store
  useEffect(() => {
    if (agentAuthorityData && isAgentAuthoritySuccess) {
      const agentAuthorityFlags = {
        issueQuote: agentAuthorityData.issueQuote || false,
        issuePolicy: agentAuthorityData.issuePolicy || false,
        canSubmitMidterms: agentAuthorityData.canSubmitMidterms || false,
      };
      setAgentAuthorityFlags(agentAuthorityFlags);
    }
  }, [agentAuthorityData, isAgentAuthoritySuccess, setAgentAuthorityFlags]);

  return {
    currentUser,
    policyData,
    agentAuthorityData,
    permissionsData,
    isPolicySuccess,
    isAgentAuthoritySuccess,
    isPermissionsSuccess,
    isCurrentUserSuccess,
    isPolicyAssignedToMe,
    isPolicyReferredToMe,
  };
};
